// Swipe between pages: drag or flick left for the next page, right for the previous (swapped in
// right-to-left text), or press the arrow keys. A page can be gated (`canAdvance` false): the
// forward swipe then bounces back and calls `onBlocked`, so nothing is skipped. Only the current
// page is mounted, and it follows the finger while dragging.
import { useState } from "react";
import { View } from "tamagui";
import { GestureArea } from "./gestures/GestureArea";
import { haptic } from "./gestures/haptics";

export interface PagerProps {
  index: number;
  count: number;
  onIndexChange: (index: number) => void;
  /** False at a gate: forward is refused and `onBlocked` runs instead. */
  canAdvance?: boolean;
  onBlocked?: (() => void) | undefined;
  /** No swiping (a question awaiting its answer); the arrow keys still work. */
  locked?: boolean;
  /** The pager's accessible name. */
  label: string;
  children: React.ReactNode;
}

const isRtl = (): boolean => document.documentElement.dir === "rtl";

export function Pager({
  index,
  count,
  onIndexChange,
  canAdvance = true,
  onBlocked,
  locked = false,
  label,
  children,
}: PagerProps): React.JSX.Element {
  const [dx, setDx] = useState(0);

  const forward = (): void => {
    if (index + 1 >= count) return;
    if (!canAdvance) {
      haptic("select");
      onBlocked?.();
      return;
    }
    onIndexChange(index + 1);
  };
  const back = (): void => {
    if (index > 0) onIndexChange(index - 1);
  };
  const [onLeft, onRight] = isRtl() ? [back, forward] : [forward, back];

  return (
    <GestureArea
      axis="x"
      disabled={locked}
      flex={1}
      role="group"
      aria-roledescription="carousel"
      aria-label={label}
      onSwipe={{ left: onLeft, right: onRight }}
      onDrag={({ dx: moved, active }) => setDx(active ? moved / 3 : 0)}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === "ArrowRight") (isRtl() ? back : forward)();
        if (event.key === "ArrowLeft") (isRtl() ? forward : back)();
      }}
    >
      <View
        flex={1}
        style={{
          transform: `translateX(${dx}px)`,
          transition: dx === 0 ? "transform var(--durBase) ease-out" : "none",
        }}
      >
        {children}
      </View>
    </GestureArea>
  );
}
